import React, { useMemo, useState, forwardRef } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import th from "date-fns/locale/th";
import { format } from "date-fns";

registerLocale("th", th);

/** =========================
 *  Helpers
 ========================= */
const MIN_YEAR_AD_DEFAULT = 2019; // พ.ศ. 2562
const pad2 = (n) => String(n).padStart(2, "0");

const setStartOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};
const setEndOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const endOfYear = (d) => new Date(d.getFullYear(), 11, 31);

const fmtBEDate = (d) => {
    if (!d) return "";
    const dd = pad2(d.getDate());
    const mm = pad2(d.getMonth() + 1);
    const yyyy = d.getFullYear() + 543;
    return `${dd}/${mm}/${yyyy}`;
};

function buildYearsAD(minYearAD, maxYearAD) {
    return Array.from({ length: maxYearAD - minYearAD + 1 }, (_, i) => minYearAD + i);
}

const MONTHS_TH_SHORT = Array.from({ length: 12 }, (_, m) =>
    format(new Date(2024, m, 1), "MMM", { locale: th })
);

// preset range
function computeRangeByPreset(key, pickedDate, today) {
    const base = pickedDate instanceof Date ? pickedDate : new Date();

    if (key === "1day") {
        return { start: setStartOfDay(base), end: setEndOfDay(base) };
    }

    if (key === "7days") {
        const end = setEndOfDay(base);
        const start = setStartOfDay(
            new Date(end.getFullYear(), end.getMonth(), end.getDate() - 6)
        );
        return { start, end };
    }

    if (key === "1month") {
        const s0 = startOfMonth(base);
        const e0 = endOfMonth(base);
        let start = setStartOfDay(s0);
        let end = setEndOfDay(e0);
        if (today && end > today) end = setEndOfDay(today);
        return { start, end };
    }

    if (key === "1year") {
        const s0 = startOfYear(base);
        const e0 = endOfYear(base);
        let start = setStartOfDay(s0);
        let end = setEndOfDay(e0);
        if (today && end > today) end = setEndOfDay(today);
        return { start, end };
    }

    return { start: setStartOfDay(base), end: setEndOfDay(base) };
}

/** =========================
 *  ThaiBEDateRangePicker
 *  - ใช้ซ้ำข้ามโปรเจค
 *  - คุมด้วย startDate / endDate เท่านั้น
 *  - BE UI (พ.ศ.)
 *  - รองรับ preset: 1day / 7days / 1month / 1year (optional)
 ========================= */
const PRESET_KEYS = ["1day", "7days", "1month", "1year"];

export default function ThaiBEDateRangePicker({
    modeKey = "", // "" = custom range, หรือ 1day/7days/1month/1year
    today: todayProp,
    Icon,
    startDate,
    endDate,
    onChange, // ({ startDate, endDate }) => void
    onManualRangePick, // (state: "start-only" | "done") => void

    minYearAD = MIN_YEAR_AD_DEFAULT,
    minDate, // optional override
    maxDate, // optional override

    inputClassName = "py-1 text-[14px] text-[#4B5563] placeholder:text-[#4B5563] bg-transparent rounded-sm px-2 outline-none w-48 text-center",
}) {
    const today = useMemo(() => (todayProp instanceof Date ? todayProp : new Date()), [todayProp]);

    const maxYearAD = today.getFullYear();
    const maxMonth = today.getMonth();

    const yearsAD = useMemo(() => buildYearsAD(minYearAD, maxYearAD), [minYearAD, maxYearAD]);

    const isPreset = PRESET_KEYS.includes(modeKey);
    const isCustomRange = !isPreset;

    const [open, setOpen] = useState(false);

    const displayValue = useMemo(() => {
        if (modeKey === "1day") return fmtBEDate(startDate || today);
        if (modeKey === "7days") return `${fmtBEDate(startDate)} - ${fmtBEDate(endDate)}`;
        if (startDate && endDate) return `${fmtBEDate(startDate)} - ${fmtBEDate(endDate)}`;
        if (startDate) return `${fmtBEDate(startDate)} -`;
        return "";
    }, [modeKey, startDate, endDate, today]);

    const BEInput = useMemo(
        () =>
            forwardRef(({ onClick, ...rest }, ref) => (
                <div>
                    <Icon className="inline-block text-slate-600" />
                    <input
                        {...rest}
                        ref={ref}
                        readOnly
                        value={displayValue}
                        onClick={(e) => {
                            onClick?.(e);
                            setOpen(true);
                        }}
                        className={inputClassName}
                        placeholder="เลือกช่วงวันที่"
                    />
                </div>
            )),
        [displayValue, inputClassName]
    );

    // preset selected date
    const selectedDate = useMemo(() => {
        if (!isPreset) return null;
        return startDate || today;
    }, [isPreset, startDate, today]);

    const applyRangeAndClose = (pickedDate) => {
        const { start, end } = computeRangeByPreset(modeKey, pickedDate, today);
        onChange?.({ startDate: start, endDate: end });
        setOpen(false);
    };

    // ====== ✅ โหมด "1 ปี" (select ปี) ======
    if (modeKey === "1year") {
        const yearAD = (startDate || today).getFullYear();

        return (
            <div className="flex flex-col items-center gap-1">
                <select
                    className="w-36 rounded-md border border-slate-200 bg-white px-3 py-2 text-[14px]"
                    value={yearAD}
                    onChange={(e) => {
                        const yAD = Number(e.target.value);
                        const s0 = setStartOfDay(new Date(yAD, 0, 1));
                        let e0 = setEndOfDay(new Date(yAD, 11, 31));
                        if (e0 > today) e0 = setEndOfDay(today);
                        onChange?.({ startDate: s0, endDate: e0 });
                    }}
                >
                    {yearsAD.map((y) => (
                        <option key={y} value={y}>
                            {y + 543}
                        </option>
                    ))}
                </select>
            </div>
        );
    }

    // ====== ✅ โหมด "1 เดือน" (select เดือน + ปี) ======
    if (modeKey === "1month") {
        const current = startDate || today;
        const yearAD = current.getFullYear();
        const month = current.getMonth();

        const isAllMonths =
            startDate &&
            endDate &&
            startDate.getMonth() === 0 &&
            startDate.getDate() === 1 &&
            endDate.getMonth() === 11 &&
            endDate.getDate() === 31;

        const monthValue = isAllMonths ? -1 : month;

        const monthOptions = [
            { value: -1, label: "เดือนทั้งหมด" },
            ...MONTHS_TH_SHORT.map((label, idx) => ({ value: idx, label })),
        ];

        return (
            <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-3">
                    {/* เดือน */}
                    <select
                        className="w-36 rounded-md border border-slate-200 bg-white px-3 py-2 text-[14px]"
                        value={monthValue}
                        onChange={(e) => {
                            const m = Number(e.target.value);

                            if (m === -1) {
                                const s0 = setStartOfDay(new Date(yearAD, 0, 1));
                                let e0 = setEndOfDay(new Date(yearAD, 11, 31));
                                if (e0 > today) e0 = setEndOfDay(today);
                                onChange?.({ startDate: s0, endDate: e0 });
                                return;
                            }

                            const s0 = setStartOfDay(new Date(yearAD, m, 1));
                            let e0 = setEndOfDay(endOfMonth(new Date(yearAD, m, 1)));
                            if (e0 > today) e0 = setEndOfDay(today);
                            onChange?.({ startDate: s0, endDate: e0 });
                        }}
                    >
                        {monthOptions.map((opt) => {
                            const disabled = yearAD === maxYearAD && opt.value >= 0 && opt.value > maxMonth;
                            return (
                                <option key={opt.value} value={opt.value} disabled={disabled}>
                                    {opt.label}
                                </option>
                            );
                        })}
                    </select>

                    {/* ปี */}
                    <select
                        className="w-28 rounded-md border border-slate-200 bg-white px-3 py-2 text-[14px]"
                        value={yearAD}
                        onChange={(e) => {
                            const yAD = Number(e.target.value);

                            if (monthValue === -1) {
                                const s0 = setStartOfDay(new Date(yAD, 0, 1));
                                let e0 = setEndOfDay(new Date(yAD, 11, 31));
                                if (e0 > today) e0 = setEndOfDay(today);
                                onChange?.({ startDate: s0, endDate: e0 });
                                return;
                            }

                            const s0 = setStartOfDay(new Date(yAD, month, 1));
                            let e0 = setEndOfDay(endOfMonth(new Date(yAD, month, 1)));
                            if (e0 > today) e0 = setEndOfDay(today);
                            onChange?.({ startDate: s0, endDate: e0 });
                        }}
                    >
                        {yearsAD.map((y) => (
                            <option key={y} value={y}>
                                {y + 543}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }

    // ====== ✅ โหมด custom / 1day / 7days (DatePicker) ======
    const effectiveMinDate = minDate || new Date(minYearAD, 0, 1);
    const effectiveMaxDate = maxDate || today;

    return (
        <DatePicker
            locale="th"
            calendarStartDay={1}
            minDate={effectiveMinDate}
            maxDate={effectiveMaxDate}
            isClearable={false}
            open={open}
            onClickOutside={() => setOpen(false)}
            onCalendarClose={() => setOpen(false)}
            shouldCloseOnSelect={false}
            customInput={<BEInput />}

            selectsRange={isCustomRange}
            startDate={isCustomRange ? startDate : undefined}
            endDate={isCustomRange ? endDate : undefined}
            selected={!isCustomRange ? selectedDate : undefined}

            // ปีบน grid เป็น พ.ศ.
            renderYearContent={(year) => year + 543}

            // header เดือน/ปี (ปีเป็น พ.ศ. แต่ส่งค่า ค.ศ.)
            renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
            }) => {
                const yearAD = date.getFullYear();
                const month = date.getMonth();

                return (
                    <div className="flex items-center justify-between px-2 py-2">
                        <button
                            type="button"
                            onClick={decreaseMonth}
                            disabled={prevMonthButtonDisabled}
                            className="px-2 text-slate-600 disabled:opacity-30"
                        >
                            ‹
                        </button>

                        <div className="flex items-center gap-2">
                            <select
                                value={month}
                                onChange={(e) => changeMonth(Number(e.target.value))}
                                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[13px]"
                            >
                                {MONTHS_TH_SHORT.map((label, m) => (
                                    <option key={`${label}-${m}`} value={m}>
                                        {label}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={yearAD}
                                onChange={(e) => changeYear(Number(e.target.value))}
                                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[13px]"
                            >
                                {yearsAD.map((y) => (
                                    <option key={y} value={y}>
                                        {y + 543}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            type="button"
                            onClick={increaseMonth}
                            disabled={nextMonthButtonDisabled}
                            className="px-2 text-slate-600 disabled:opacity-30"
                        >
                            ›
                        </button>
                    </div>
                );
            }}

            onChange={(value) => {
                // custom range
                if (isCustomRange) {
                    const [s, e] = value || [];
                    if (s && !e) {
                        onChange?.({ startDate: setStartOfDay(s), endDate: null });
                        onManualRangePick?.("start-only");
                        return;
                    }
                    if (s && e) {
                        onChange?.({ startDate: setStartOfDay(s), endDate: setEndOfDay(e) });
                        onManualRangePick?.("done");
                        setOpen(false);
                        return;
                    }
                    return;
                }

                // preset: 1 click
                const picked = value instanceof Date ? value : new Date();
                applyRangeAndClose(picked);
            }}
        />
    );
}
